import { useRef } from 'react';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useCartDispatch,
  useCartState,
  CartActionType,
  FarmProduct,
} from 'public/context/CartContext';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useCoupons } from 'public/components/DefaultLayout/CartSummary/useCoupons';
import { CouponDiscounts } from 'interfaces/Coupon';
import useMarketType from '../utils/useMarketType';

export const useRefetchCartProducts = () => {
  const intl = useIntl();
  const dispatch = useCartDispatch();
  const {
    farmProducts: contextFarmProducts,
    priceTotal: contextPriceTotal,
    cartId,
  } = useCartState();
  const { coupons: contextCoupons } = useCoupons();
  const { marketType } = useMarketType();

  const notificationApi = notification.useNotification()[0];

  const requestDataRef = useRef({
    farmProducts: contextFarmProducts,
    priceTotal: contextPriceTotal,
    coupons: contextCoupons,
    marketType: marketType,
    cartId: cartId,
  });

  const openNotificationCartUpdated = () => {
    notificationApi.warning({
      message: intl.formatMessage({ id: 'cart.updated_message' }),
      description: intl.formatMessage({ id: 'cart.updated_description' }),
      showProgress: true,
      pauseOnHover: true,
      duration: 10,
    });
  };

  const { refetch: triggerRefetch } = useQueryApiClient({
    request: {
      url: `api/v2/products/cart/refetch`,
      method: 'POST',
      get data() {
        return requestDataRef.current;
      },
    },
    onSuccess: (response) => {
      if (response.cartUpdated) openNotificationCartUpdated();
      dispatch({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: response.marketType,
      });
    },
  });

  const refetchCartProducts = (
    farmProductsOverride?: FarmProduct[],
    priceTotalOverride?: number,
    couponsOverride?: CouponDiscounts[],
    cartId?: number
  ) => {
    const farmProducts = farmProductsOverride ?? contextFarmProducts;
    const priceTotal = priceTotalOverride ?? contextPriceTotal;
    const coupons = couponsOverride ?? contextCoupons;

    if (!Array.isArray(farmProducts) || farmProducts.length === 0) {
      return;
    }

    requestDataRef.current = {
      farmProducts,
      priceTotal,
      coupons,
      marketType: requestDataRef.current.marketType,
      cartId: cartId,
    };

    triggerRefetch();
  };

  return { refetchCartProducts, openNotificationCartUpdated };
};
